import React from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Button, Flex, Text, TextArea } from '@opendoor/bricks/core';
import { Value } from 'classnames';

import {
  OdProtosListingsData_CustomerDeclineReason as CustomerDeclineReason,
  OdProtosListingsData_LwcProduct_State as LwcProductState,
  OdProtosListingsData_NoteType as NOTE_TYPE,
} from '../../__generated__/athenaGlobalTypes';
import { useCustomerDecline } from '../../graphql/athena/mutations/CustomerDecline';
import { useSelectDirectSale } from '../../graphql/athena/mutations/SelectDirectSale';
import { useSetNote } from '../../graphql/athena/mutations/SetNote';
import {
  OPTED_FOR_DIRECT_SALE_LWC_PRODUCT_STATES,
  SELECTED_LISTING_LWC_PRODUCT_STATES,
} from '../../helpers/getAllLeadData';
import { NOTE_TYPES } from '../../routes/LwcDashboard/SetNotesForm';

interface ConvertedToSellDirectFormProps {
  lwcProductId: string;
  lwcProductState: LwcProductState;
  conversionNote?: string;
}

/**
 * This should be used to update the status of a listing to converted to sell direct.
 * An agent should add their note and then trigger the mutation to set the note as well as trigger the conversion of the state.
 */
export const ConvertedToSellDirectForm: React.FC<ConvertedToSellDirectFormProps> = ({
  lwcProductId,
  lwcProductState,
  conversionNote,
}) => {
  const [showConvertedToSellDirectForm, setShowConvertedToSellDirectForm] = React.useState(false);
  // Used for post listing agreement signed selecting of direct sale
  const { selectDirectSale, error: conversionError } = useSelectDirectSale();
  // Used for pre listing agreement signed selecting of direct sale
  const { customerDecline, error: customerDeclineError } = useCustomerDecline();

  const { setNote, error: noteError } = useSetNote();
  const [formData, setFormData] = React.useState(conversionNote ?? '');
  const alreadyOptedForSellDirect =
    OPTED_FOR_DIRECT_SALE_LWC_PRODUCT_STATES.includes(lwcProductState);
  const alreadyConvertedToListing = SELECTED_LISTING_LWC_PRODUCT_STATES.includes(lwcProductState);

  const handleConvertedToSellDirect = async () => {
    try {
      // Order this way to ensure that the note is set before the conversion is recorded.
      // This is because we want to include the note in the lead pass back notification
      // email to CEPs.
      await setNote(lwcProductId, formData, NOTE_TYPE.LEAD_PASS_BACK_NOTE);

      if (alreadyConvertedToListing) {
        await selectDirectSale(lwcProductId);
      } else {
        await customerDecline(lwcProductId, CustomerDeclineReason.DECLINE_REASON_DIRECT_SALE_ONLY);
      }
      showToast(
        'info',
        `Successfully set ${
          NOTE_TYPES[NOTE_TYPE.LEAD_PASS_BACK_NOTE]
        } and converted to sell direct`,
        'lwc-dashboard-toast',
        {
          toastOptions: { autoClose: 3000 },
        },
      );
    } catch (e) {
      showToast(
        'error',
        `Error setting notes ${noteError} and converting to sell direct ${conversionError} ${customerDeclineError}}`,
        'lwc-dashboard-toast',
        {
          toastOptions: { autoClose: false },
        },
      );
    }
  };

  const handleConversionNoteUpdate = async () => {
    try {
      setNote(lwcProductId, formData, NOTE_TYPE.LEAD_PASS_BACK_NOTE);
      showToast(
        'info',
        `Successfully set ${NOTE_TYPES[NOTE_TYPE.LEAD_PASS_BACK_NOTE]}`,
        'lwc-dashboard-toast',
        {
          toastOptions: { autoClose: 3000 },
        },
      );
    } catch (e) {
      showToast('error', `Error setting notes ${noteError}`, 'lwc-dashboard-toast', {
        toastOptions: { autoClose: false },
      });
    }
  };

  const handleTextChange = (
    _event: React.ChangeEvent<HTMLTextAreaElement>,
    _name: string,
    value?: Value,
  ) => {
    setFormData((value as string) ?? null);
  };

  return (
    <Flex flexDirection="column" my={3} gridGap={3}>
      <Text fontWeight="semibold">Converted to sell direct</Text>
      {!showConvertedToSellDirectForm && (
        <Button
          aria-label="Show conversion form"
          analyticsName="show-conversion-to-sell-direct-form"
          onClick={() => {
            setShowConvertedToSellDirectForm(true);
          }}
        >
          {alreadyOptedForSellDirect
            ? 'Update sell direct conversion note'
            : 'Convert to sell direct'}
        </Button>
      )}

      {showConvertedToSellDirectForm && (
        <Flex flexDirection="column" gridGap={3}>
          <Text aria-label="Conversion to sell direct input label">
            What is the reason for the conversion?
          </Text>
          <TextArea
            aria-label="Set note"
            content={formData}
            onChange={handleTextChange}
            name={NOTE_TYPE.LEAD_PASS_BACK_NOTE}
            analyticsName={`noteType-${NOTE_TYPE.LEAD_PASS_BACK_NOTE}`}
            id={NOTE_TYPE.LEAD_PASS_BACK_NOTE}
          />
          <Flex gridGap={3} flexDirection={['column', null, 'row']} my={3}>
            {alreadyOptedForSellDirect ? (
              <Button
                aria-label="Update conversion note"
                analyticsName="update-sell-direct-conversion-note"
                disabled={!formData || formData === conversionNote}
                onClick={() => {
                  handleConversionNoteUpdate();
                }}
              >
                Update sell direct conversion note
              </Button>
            ) : (
              <Button
                aria-label="Submit conversion to sell direct offer"
                analyticsName="set-conversion-to-sell-direct"
                disabled={!formData}
                onClick={() => {
                  handleConvertedToSellDirect();
                  setShowConvertedToSellDirectForm(false);
                }}
                backgroundColor={'green50'}
                sx={{
                  ':hover': {
                    backgroundColor: 'lightgrey',
                    color: 'green50',
                  },
                }}
              >
                Confirm sell direct conversion
              </Button>
            )}
            <Button
              aria-label="Cancel conversion to sell direct offer"
              analyticsName="cancel-conversion-to-sell-direct"
              onClick={() => {
                setShowConvertedToSellDirectForm(false);
              }}
              backgroundColor={'red50'}
              sx={{
                ':hover': {
                  backgroundColor: 'lightgrey',
                  color: 'red50',
                },
              }}
            >
              Cancel
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
